import React, { useMemo } from "react";
import { NavigateFunction } from "react-router-dom";
import { EvalResult, ActiveRun } from "../../services/evals";
import { BatchRunRow } from "./BatchRunRow";

interface BatchRunsTableProps {
  evalResults: EvalResult[] | undefined;
  activeRun: ActiveRun | null;
  expandedBatchRun: string | null;
  expandedBatchRunResultId: string | null;
  setExpandedBatchRun: (id: string | null) => void;
  setExpandedBatchRunResultId: (id: string | null) => void;
  navigate: NavigateFunction;
}

export const BatchRunsTable: React.FC<BatchRunsTableProps> = ({
  evalResults,
  activeRun,
  expandedBatchRun,
  expandedBatchRunResultId,
  setExpandedBatchRun,
  setExpandedBatchRunResultId,
  navigate,
}) => {
  // Memoize grouping results by run_name to prevent re-computation on every render
  const batchRuns = useMemo(() => {
    if (!Array.isArray(evalResults)) return undefined;
    return evalResults.reduce(
      (acc, result) => {
        if (result.run_name) {
          if (!acc[result.run_name]) {
            acc[result.run_name] = [];
          }
          acc[result.run_name].push(result);
        }
        return acc;
      },
      {} as Record<string, EvalResult[]>
    );
  }, [evalResults]);

  // Memoize sorting to prevent re-computation on every render
  const batchRunEntries = useMemo(() => {
    if (!batchRuns) return [];
    return Object.entries(batchRuns).sort((a, b) => {
      const aDate = new Date(a[1][0].created_at).getTime();
      const bDate = new Date(b[1][0].created_at).getTime();
      return bDate - aDate;
    });
  }, [batchRuns]);

  if (batchRunEntries.length === 0) {
    return (
      <div className="al-text-center al-py-12 al-text-gray-500">
        <p>
          No batch runs yet. Select test cases and click &ldquo;Run Selected&rdquo; to start a batch
          run.
        </p>
      </div>
    );
  }

  return (
    <div className="al-border al-border-gray-200 al-rounded-md al-overflow-hidden">
      <table className="al-w-full al-divide-y al-divide-gray-200 al-table-fixed">
        <colgroup>
          <col className="al-w-[25%]" />
          <col className="al-w-[12%]" />
          <col className="al-w-[10%]" />
          <col className="al-w-[10%]" />
          <col className="al-w-[10%]" />
          <col className="al-w-[13%]" />
          <col className="al-w-[20%]" />
        </colgroup>
        <thead className="al-bg-gray-50">
          <tr>
            <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
              Run Name
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
              Status
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
              Total
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
              Passed
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
              Failed
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
              Avg Score
            </th>
            <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
              Executed At
            </th>
          </tr>
        </thead>
        <tbody className="al-bg-white al-divide-y al-divide-gray-200">
          {batchRunEntries.map(([runName, results]) => (
            <BatchRunRow
              key={runName}
              runName={runName}
              results={results}
              activeRun={activeRun}
              expandedBatchRun={expandedBatchRun}
              expandedBatchRunResultId={expandedBatchRunResultId}
              setExpandedBatchRun={setExpandedBatchRun}
              setExpandedBatchRunResultId={setExpandedBatchRunResultId}
              navigate={navigate}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
